import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ProgressBar } from '@/components/ui/ProgressBar';
import type { McpPermission } from '@/features/integrations/integrations.types';
import { getIntegrationIcon } from '@/integrations/integration-icons';

import { APP_SETUP_BY_TEMPLATE } from './app-setup';
import { AppSetupSteps } from './app-setup-card';
import { Callout } from './callout';
import {
  CapabilityRow,
  CheckRow,
  ListSection,
  type CheckState,
} from './connect-checklist';
import { DeviceNoteCallout } from './device-note-callout';
import { DialogHeader } from './dialog-header';
import { ModalCard } from './modal-card';
import {
  MCP_PERMISSIONS,
  permissionsShownBeforeConnect,
} from './server-permissions';
import { MissingPermissionsDetails } from './server-permissions-callout';
import {
  oneClickProblemCopy,
  type OneClickConnectForm,
} from './use-one-click-connect';

import type { OneClickServer } from './one-click-server';

export function DownloadRow({ form }: { form: OneClickConnectForm }) {
  const { download, phase } = form;
  // Only a real download reports bytes here; servers that ship inside the app
  // and runtimes already on disk show no bar
  if (!download) return null;

  const { receivedBytes, totalBytes } = download;
  const done = phase !== 'preparing';
  const fraction = done
    ? 1
    : totalBytes
      ? Math.min(receivedBytes / totalBytes, 1)
      : 0;
  const label = done ? 'Downloaded' : 'Downloading';
  // Without a declared total there is no honest percentage to show, so the
  // label carries the state on its own.
  const percent = done || totalBytes ? `${Math.round(fraction * 100)}%` : null;

  return (
    <YStack className="gap-2">
      <XStack className="items-center justify-between gap-2">
        <span className="text-xs text-foreground-subtle">{label}</span>
        {percent && (
          <span className="font-mono text-[10px] text-foreground-muted">
            {percent}
          </span>
        )}
      </XStack>
      <ProgressBar value={fraction} label={label} />
    </YStack>
  );
}

function PermissionCheckRow({ permission }: { permission: McpPermission }) {
  const { label, use, useGranted, requestAccess } = MCP_PERMISSIONS[permission];
  const granted = useGranted();
  const state: CheckState =
    granted === undefined ? 'checking' : granted ? 'ok' : 'blocked';

  return (
    <CheckRow
      check={{
        title: label,
        state,
        detail:
          state === 'ok'
            ? use
            : state === 'blocked'
              ? `Off. Turn on Littlebird in Privacy & Security → ${label}. If macOS asks, quit and reopen Littlebird.`
              : 'Checking…',
        action: state === 'blocked' && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => void requestAccess()}
          >
            Open System Settings
          </Button>
        ),
      }}
    />
  );
}

export const OneClickConnectModal = ({
  server,
  form,
  onCancel,
}: {
  server: OneClickServer;
  form: OneClickConnectForm;
  onCancel: () => void;
}): React.ReactElement => {
  const IntegrationIcon = getIntegrationIcon(server.id);
  const appSetup = APP_SETUP_BY_TEMPLATE[server.id];
  const primaryLabel = form.problem ? 'Try again' : 'Connect';
  const upfrontPermissions = permissionsShownBeforeConnect(
    server.requiredPermissions
  );
  const loadingText =
    form.phase === 'preparing'
      ? 'Getting ready…'
      : form.phase === 'saving'
        ? 'Finishing…'
        : 'Connecting…';

  return (
    <ModalCard onClose={onCancel} closeDisabled={!form.canCancel}>
      <DialogHeader
        icon={<IntegrationIcon className="size-5" />}
        title={`Connect ${server.name}`}
        subtitle={<span className="block">{server.description}</span>}
      />
      <YStack className="gap-4 p-6">
        <DownloadRow form={form} />

        {server.capabilities.length > 0 && (
          <ListSection label="Littlebird will be able to">
            {server.capabilities.map((capability) => (
              <CapabilityRow key={capability} title={capability} />
            ))}
          </ListSection>
        )}

        {upfrontPermissions.length > 0 && (
          <ListSection label="Checks on this Mac">
            {upfrontPermissions.map((permission) => (
              <PermissionCheckRow key={permission} permission={permission} />
            ))}
          </ListSection>
        )}

        {/* The app setup card restates the catalog's notes in full */}
        {!appSetup && server.notes.length > 0 ? (
          <DeviceNoteCallout lines={server.notes} />
        ) : null}

        {form.problem && (
          <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
            <YStack className="min-w-0 gap-1.5">
              <span className="text-sm font-medium text-foreground">
                {oneClickProblemCopy(form.problem, server.name)}
              </span>
              {form.missingPermissions.length > 0 && (
                <MissingPermissionsDetails
                  missingPermissions={form.missingPermissions}
                />
              )}
            </YStack>
          </Callout>
        )}

        {appSetup && (
          <AppSetupSteps appSetup={appSetup} steps={appSetup.connectSteps} />
        )}
      </YStack>
      <XStack className="items-center justify-end gap-2 border-t border-border p-4 px-6">
        <Button
          variant="ghost"
          size="sm"
          disabled={!form.canCancel}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!form.canConnect}
          isLoading={form.busy}
          loadingText={loadingText}
          onClick={() => void form.connect()}
        >
          {primaryLabel}
        </Button>
      </XStack>
    </ModalCard>
  );
};
